// MCHD Room Scheduler - Advanced Holiday Form Component
// Modern form for creating and editing holidays with validation

import { useEffect } from 'react';
import {
  Paper,
  Stack,
  TextInput,
  Select,
  Button,
  Group,
  Text,
  Alert,
  Box,
  Flex,
  Badge,
  Transition,
  Loader,
} from '@mantine/core';
import {
  IconCalendar,
  IconBuilding,
  IconTag,
  IconInfoCircle,
  IconCheck,
  IconX,
  IconCalendarEvent,
  IconAlertCircle,
} from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { useBuildings } from '../../hooks/admin/useHolidays';
import { notifications } from '@mantine/notifications';
import { isAfter, isBefore, startOfDay } from 'date-fns';

// =============================================================================
// TYPES
// =============================================================================

interface HolidayFormProps {
  initialValues?: {
    id?: string;
    buildingId: string;
    dateDay: string;
    label: string;
  };
  onSubmit: (values: HolidayFormData) => Promise<void>;
  onCancel: () => void;
  mode: 'create' | 'edit';
  isLoading?: boolean;
  className?: string;
}

interface HolidayFormData {
  buildingId: string;
  dateDay: string;
  label: string;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * Advanced Holiday Form Component
 * Features:
 * - Modern form design with validation
 * - Real-time validation feedback
 * - Building selection with search
 * - Date picker with constraints
 * - Label validation with character count
 * - Loading states and error handling
 * - Responsive design
 */
export function HolidayForm({
  initialValues,
  onSubmit,
  onCancel,
  mode,
  isLoading = false,
  className,
}: HolidayFormProps): JSX.Element {
  
  // Hooks
  const { data: buildingsData, isLoading: buildingsLoading } = useBuildings();
  
  // Form setup
  const form = useForm({
    initialValues: {
      buildingId: initialValues?.buildingId || '',
      dateDay: initialValues?.dateDay || '',
      label: initialValues?.label || '',
    },
    validate: {
      buildingId: (value: string) => {
        if (!value) return 'Building is required';
        return null;
      },
      dateDay: (value: string) => {
        if (!value) return 'Date is required';
        
        const selectedDate = new Date(value);
        const today = startOfDay(new Date());
        
        // Allow past dates for historical holidays but warn
        if (isBefore(selectedDate, today)) {
          return 'Warning: This date is in the past';
        }
        
        // Check if date is too far in the future
        const maxDate = new Date();
        maxDate.setFullYear(maxDate.getFullYear() + 5);
        
        if (isAfter(selectedDate, maxDate)) {
          return 'Date cannot be more than 5 years in the future';
        }
        
        return null;
      },
      label: (value: string) => {
        if (!value) return 'Holiday label is required';
        if (value.length < 2) return 'Label must be at least 2 characters';
        if (value.length > 100) return 'Label cannot exceed 100 characters';
        
        // Check for invalid characters
        const invalidChars = /[<>{}[\]\\|`~!@#$%^&*()+=]/;
        if (invalidChars.test(value)) {
          return 'Label contains invalid characters';
        }
        
        return null;
      },
    },
    transformValues: (values) => ({
      buildingId: values.buildingId,
      dateDay: values.dateDay,
      label: values.label.trim(),
    }),
  });

  // Update form when initial values change
  useEffect(() => {
    if (initialValues) {
      form.setValues({
        buildingId: initialValues.buildingId,
        dateDay: initialValues.dateDay,
        label: initialValues.label,
      });
    }
  }, [initialValues]);

  // Event handlers
  const handleSubmit = async (values: HolidayFormData) => {
    try {
      await onSubmit(values);
      notifications.show({
        title: mode === 'create' ? 'Holiday Created' : 'Holiday Updated',
        message: `The holiday "${values.label}" has been ${mode === 'create' ? 'created' : 'updated'} successfully.`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: `Failed to ${mode === 'create' ? 'create' : 'update'} holiday. Please try again.`,
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  const handleCancel = () => {
    form.reset();
    onCancel();
  };

  // Loading state
  if (buildingsLoading) {
    return (
      <Paper radius="lg" p="xl" {...(className && { className })}>
        <Flex align="center" justify="center" h={300}>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text c="dimmed">Loading form data...</Text>
          </Stack>
        </Flex>
      </Paper>
    );
  }

  // Error state
  if (!buildingsData?.success) {
    return (
      <Paper radius="lg" p="xl" {...(className && { className })}>
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Form Unavailable"
          color="red"
        >
          Failed to load form data. Please refresh the page and try again.
        </Alert>
      </Paper>
    );
  }

  const buildings = buildingsData.data || [];
  const selectedBuilding = buildings.find(b => b.id === form.values.buildingId);
  const isPastDate = form.values.dateDay && isBefore(new Date(form.values.dateDay), startOfDay(new Date()));

  return (
    <Paper radius="lg" p="xl" shadow="sm" {...(className && { className })}>
      <Stack gap="xl">
        {/* Header */}
        <Box>
          <Group gap="md" mb="sm">
            <IconCalendarEvent 
              size={24} 
              color={mode === 'create' ? 'var(--mantine-color-green-6)' : 'var(--mantine-color-blue-6)'} 
            />
            <Text size="xl" fw={600}>
              {mode === 'create' ? 'Create New Holiday' : 'Edit Holiday'}
            </Text>
          </Group>
          <Text size="sm" c="dimmed">
            {mode === 'create' 
              ? 'Add a new holiday to the hospital calendar'
              : 'Update the holiday information'
            }
          </Text>
        </Box>

        {/* Form */}
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="lg">
            {/* Building Selection */}
            <Box>
              <Text size="sm" fw={500} mb="xs">
                Building <Text component="span" c="red">*</Text>
              </Text>
              <Select
                placeholder="Select a building"
                data={buildings.map(building => ({
                  value: building.id,
                  label: building.name,
                  description: building.description,
                }))}
                leftSection={<IconBuilding size={16} />}
                searchable
                nothingFoundMessage="No buildings found"
                {...form.getInputProps('buildingId')}
                size="md"
                radius="md"
              />
              
              {/* Building info */}
              {selectedBuilding && (
                <Transition mounted={!!selectedBuilding} transition="slide-down" duration={200}>
                  {(styles) => (
                    <Box style={styles} mt="xs">
                      <Badge variant="light" color="blue" size="sm">
                        {selectedBuilding.description || 'No description available'}
                      </Badge>
                    </Box>
                  )}
                </Transition>
              )}
            </Box>

            {/* Date Selection */}
            <Box>
              <Text size="sm" fw={500} mb="xs">
                Holiday Date <Text component="span" c="red">*</Text>
              </Text>
              <TextInput
                placeholder="YYYY-MM-DD"
                leftSection={<IconCalendar size={16} />}
                type="date"
                {...form.getInputProps('dateDay')}
                size="md"
                radius="md"
              />
              
              {/* Date warnings */}
              {isPastDate && (
                <Transition mounted={isPastDate} transition="slide-down" duration={200}>
                  {(styles) => (
                    <Alert
                      style={styles}
                      mt="xs"
                      icon={<IconInfoCircle size={16} />}
                      title="Past Date"
                      color="yellow"
                      variant="light"
                    >
                      This date is in the past. Historical holidays are allowed but may affect scheduling.
                    </Alert>
                  )}
                </Transition>
              )}
            </Box>

            {/* Holiday Label */}
            <Box>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>
                  Holiday Label <Text component="span" c="red">*</Text>
                </Text>
                <Text size="xs" c="dimmed">
                  {form.values.label.length}/100 characters
                </Text>
              </Group>
              <TextInput
                placeholder="Enter holiday name (e.g., New Year's Day, Eid al-Fitr)"
                leftSection={<IconTag size={16} />}
                maxLength={100}
                {...form.getInputProps('label')}
                size="md"
                radius="md"
              />
              
              {/* Label suggestions */}
              {!form.values.label && (
                <Box mt="xs">
                  <Text size="xs" c="dimmed" mb="xs">Common holiday labels:</Text>
                  <Group gap="xs">
                    {['New Year\'s Day', 'Eid al-Fitr', 'National Day', 'Independence Day', 'Labor Day'].map(suggestion => (
                      <Badge
                        key={suggestion}
                        variant="light"
                        size="sm"
                        style={{ cursor: 'pointer' }}
                        onClick={() => form.setFieldValue('label', suggestion)}
                      >
                        {suggestion}
                      </Badge>
                    ))}
                  </Group>
                </Box>
              )}
            </Box>

            {/* Form Actions */}
            <Group justify="flex-end" gap="sm" mt="lg">
              <Button
                variant="subtle"
                onClick={handleCancel}
                disabled={isLoading}
                size="md"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={isLoading}
                leftSection={mode === 'create' ? <IconCheck size={16} /> : undefined}
                size="md"
              >
                {mode === 'create' ? 'Create Holiday' : 'Update Holiday'}
              </Button>
            </Group>
          </Stack>
        </form>

        {/* Form Tips */}
        <Alert
          icon={<IconInfoCircle size={16} />}
          title="Tips"
          color="blue"
          variant="light"
        >
          <Stack gap="xs">
            <Text size="sm">
              • Holidays prevent scheduling on those dates
            </Text>
            <Text size="sm">
              • Use clear, descriptive labels for better recognition
            </Text>
            <Text size="sm">
              • Past dates are allowed for historical records
            </Text>
            <Text size="sm">
              • Each building can have unique holidays
            </Text>
          </Stack>
        </Alert>
      </Stack>
    </Paper>
  );
}
